import { useEffect, useMemo, useState } from "react";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import { urlParam, useUrlState, useUrlText } from "@/shared/hooks/useUrlState";
import { setLastSelectedPurchaseOrderId } from "@/shared/utils/lastSelectedPurchaseOrder";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	IconButton,
	Paper,
	Snackbar,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import type {
	PurchaseOrder,
	PurchaseOrderDetail,
	UpsertPurchaseOrderDetailParams,
	UpsertPurchaseOrderMasterParams,
} from "../models/PurchaseOrders";
import {
	useCostCenters,
	useIsPurchaseOrdersImportMode,
	useOrderStatus,
	usePurchaseOrderDetails,
	usePurchaseOrders,
	useRemovePurchaseOrder,
	useRemovePurchaseOrderDetail,
	useRollBlanks,
	useSuppliers,
	useUpsertPurchaseOrder,
	useUpsertPurchaseOrderDetail,
} from "../hooks/usePurchaseOrders";
import { PurchaseOrderDetailsTable } from "./PurchaseOrderDetailsTable";
import { PurchaseOrderItemModal } from "./PurchaseOrderItemModal";
import { PurchaseOrderModal } from "./PurchaseOrderModal";
import { PurchaseOrdersTable } from "./PurchaseOrdersTable";
import { WriteLockTooltip } from "@/shared/components/WriteLockTooltip";
import {
	OPERATIONAL_WRITES_LOCKED_MESSAGE,
	useOperationalWritesLocked,
} from "@/shared/hooks/useOperationalWritesLocked";
import { ROLLSHOP_IMPORT_MODE_MESSAGE } from "@/shared/utils/useRollShopWriteEnabled";

// Master (PO list) on the LEFT, detail (the selected PO's items) on the RIGHT.
// Mirrors the reports/rolls master-detail layout.
const DEFAULT_LAYOUT = { left: 60, right: 40 };

const URL_SCHEMA = {
	search: urlParam.string(),
	showDeleted: urlParam.bool(false),
	purchaseOrderId: urlParam.int(),
	itemId: urlParam.int(),
};

type ConfirmTarget =
	| { kind: "po"; id: number; label: string }
	| { kind: "item"; id: number; label: string }
	| null;

export const PurchaseOrdersManager = () => {
	// Purchase Orders is RollShop-owned: in Import mode the Mill screen is
	// read-only (RollShop is the source). The SP enforces the same gate.
	// Read the setting FRESH (own query, refetch on mount/focus) so an admin's
	// change takes effect on return without a full reload — see the hook.
	const importMode = useIsPurchaseOrdersImportMode();
	const writesLocked = useOperationalWritesLocked();
	const writeDisabled = writesLocked || importMode;
	const writeDisabledReason = writesLocked
		? OPERATIONAL_WRITES_LOCKED_MESSAGE
		: importMode
			? ROLLSHOP_IMPORT_MODE_MESSAGE
			: "Read-only.";

	const [
		{ search, showDeleted, purchaseOrderId, itemId },
		setFilters,
		{ dropValues },
	] = useUrlState(URL_SCHEMA);
	// The box types locally and the URL follows 300 ms later, which is also what stops the
	// list procedure from being called once per keystroke.
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null, purchaseOrderId: null, itemId: null }),
	);
	const [error, setError] = useState<string | null>(null);
	const [warning, setWarning] = useState<string | null>(null);

	const [poModalOpen, setPoModalOpen] = useState(false);
	const [editingPO, setEditingPO] = useState<PurchaseOrder | null>(null);
	const [itemModalOpen, setItemModalOpen] = useState(false);
	const [editingItem, setEditingItem] = useState<PurchaseOrderDetail | null>(
		null,
	);
	const [confirm, setConfirm] = useState<ConfirmTarget>(null);

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "purchase-orders-master-detail-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const posQuery = usePurchaseOrders(search?.trim() || null, showDeleted);

	// The procedure already filters soft-deleted orders out via @includeDeleted.
	// Kept as a second pass so the "Show deleted" toggle stays consistent even
	// if a row arrives without the flag set.
	const visiblePOs = useMemo(() => {
		const rows = posQuery.data ?? [];
		if (showDeleted) return rows;
		return rows.filter((po) => !(po.IsDeleted ?? po.DateDeleted != null));
	}, [posQuery.data, showDeleted]);

	// Both selections travel as ids and are resolved against what the server returned, so a
	// link can never hold a copy of a row that has since changed.
	const selectedPO = useMemo(
		() =>
			purchaseOrderId === null
				? null
				: (visiblePOs.find((po) => po.Id === purchaseOrderId) ?? null),
		[visiblePOs, purchaseOrderId],
	);

	// A soft-deleted order is fully read-only — no operating on it (no items
	// add/edit/delete, no PO edit). It only shows when "Show deleted" is on.
	const selectedPODeleted =
		selectedPO?.IsDeleted ?? selectedPO?.DateDeleted != null;

	const detailsQuery = usePurchaseOrderDetails(selectedPO?.Id ?? null);
	const selectedItem = useMemo(
		() =>
			itemId === null
				? null
				: ((detailsQuery.data ?? []).find((item) => item.Id === itemId) ??
					null),
		[detailsQuery.data, itemId],
	);

	// An order or an item the current search and toggle no longer return leaves the panel
	// empty, so the key goes.
	useEffect(() => {
		if (!posQuery.isSuccess || purchaseOrderId === null) return;
		if (!visiblePOs.some((po) => po.Id === purchaseOrderId))
			dropValues("purchaseOrderId", "itemId");
	}, [posQuery.isSuccess, visiblePOs, purchaseOrderId, dropValues]);

	useEffect(() => {
		if (!detailsQuery.isSuccess || itemId === null) return;
		if (!(detailsQuery.data ?? []).some((item) => item.Id === itemId))
			dropValues("itemId");
	}, [detailsQuery.isSuccess, detailsQuery.data, itemId, dropValues]);
	const suppliersQuery = useSuppliers();
	const costCentersQuery = useCostCenters();
	const orderStatusQuery = useOrderStatus();
	const rollBlanksQuery = useRollBlanks();

	const upsertPO = useUpsertPurchaseOrder();
	const removePO = useRemovePurchaseOrder();
	const upsertItem = useUpsertPurchaseOrderDetail();
	const removeItem = useRemovePurchaseOrderDetail();

	const isSavingPO = upsertPO.isPending;
	const isSavingItem = upsertItem.isPending;

	const handleSelectPO = (po: PurchaseOrder) => {
		setFilters({ purchaseOrderId: po.Id, itemId: null });
		// Remember the PO being worked on so Rolls Received can pre-select it.
		setLastSelectedPurchaseOrderId(po.Id);
	};

	const openNewPO = () => {
		setEditingPO(null);
		setPoModalOpen(true);
	};
	const openEditPO = (po: PurchaseOrder) => {
		setFilters({ purchaseOrderId: po.Id, itemId: null });
		setEditingPO(po);
		setPoModalOpen(true);
		setLastSelectedPurchaseOrderId(po.Id);
	};
	const openNewItem = () => {
		setEditingItem(null);
		setItemModalOpen(true);
	};
	const openEditItem = () => {
		if (!selectedItem) return;
		setEditingItem(selectedItem);
		setItemModalOpen(true);
	};

	const handleSavePO = (params: UpsertPurchaseOrderMasterParams) => {
		upsertPO.mutate(params, {
			onSuccess: () => setPoModalOpen(false),
			onError: (e: unknown) => setError((e as Error).message),
		});
	};

	const handleSaveItem = (params: UpsertPurchaseOrderDetailParams) => {
		upsertItem.mutate(params, {
			onSuccess: () => setItemModalOpen(false),
			onError: (e: unknown) => setError((e as Error).message),
		});
	};

	const handleConfirmDelete = () => {
		if (!confirm) return;
		if (confirm.kind === "po") {
			removePO.mutate(confirm.id, {
				onSuccess: (softDeleteWarning) => {
					dropValues("purchaseOrderId", "itemId");
					if (softDeleteWarning) setWarning(softDeleteWarning);
				},
				onError: (e: unknown) => setError((e as Error).message),
			});
		} else {
			removeItem.mutate(confirm.id, {
				onSuccess: () => dropValues("itemId"),
				onError: (e: unknown) => setError((e as Error).message),
			});
		}
		setConfirm(null);
	};

	return (
		<Box
			sx={{
				p: 2,
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				minHeight: 0,
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600, mb: 1.5 }}>
				Purchase Orders
			</Typography>

			<Paper
				elevation={3}
				sx={{
					p: 1,
					mb: 1,
					display: "flex",
					gap: 1.5,
					alignItems: "center",
				}}
			>
				<TextField
					size="small"
					label="Search"
					placeholder="Order or requisition number"
					value={searchText}
					onChange={(e) => setSearchText(e.target.value)}
					sx={{ width: 320 }}
				/>
				<Tooltip title="Refresh">
					<span>
						<IconButton
							onClick={() => posQuery.refetch()}
							disabled={posQuery.isFetching}
							size="small"
							sx={{
								bgcolor: "primary.main",
								color: "white",
								width: 28,
								height: 28,
								"&:hover": { bgcolor: "primary.dark" },
								"&.Mui-disabled": {
									bgcolor: "action.disabledBackground",
									color: "action.disabled",
								},
							}}
						>
							<RefreshIcon sx={{ fontSize: 18 }} />
						</IconButton>
					</span>
				</Tooltip>
				<FormControlLabel
					control={
						<Checkbox
							size="small"
							checked={showDeleted}
							onChange={(e) => setFilters({ showDeleted: e.target.checked })}
						/>
					}
					label="Show deleted"
				/>
				<Box sx={{ flex: 1 }} />
				<WriteLockTooltip locked={writesLocked}>
					<Button
						size="small"
						variant="contained"
						startIcon={<AddIcon />}
						onClick={openNewPO}
						disabled={writeDisabled}
					>
						New Order
					</Button>
				</WriteLockTooltip>
			</Paper>

			<Group
				id="purchase-orders-master-detail"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%", minHeight: 0 }}
			>
				<Panel id="left" minSize={35} style={{ paddingBottom: 5 }}>
					<Box sx={{ height: "100%", pr: 1 }}>
						<Paper elevation={3} sx={{ height: "100%", overflow: "hidden" }}>
							<PurchaseOrdersTable
								purchaseOrders={visiblePOs}
								selectedId={selectedPO?.Id ?? null}
								onSelect={handleSelectPO}
								onEdit={openEditPO}
								onDelete={(po) =>
									setConfirm({
										kind: "po",
										id: po.Id,
										label: po.Number?.trim() ?? "",
									})
								}
								writeDisabled={writeDisabled}
								writeDisabledReason={writeDisabledReason}
								showDeleted={showDeleted}
								loading={posQuery.isFetching}
							/>
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 6,
							height: 40,
							borderRadius: 3,
							backgroundColor: "divider",
							"&:hover": { backgroundColor: "primary.main" },
						}}
					/>
				</Separator>

				<Panel id="right" minSize={25} style={{ paddingBottom: 5 }}>
					<Box sx={{ height: "100%", pl: 1 }}>
						<Paper
							elevation={3}
							sx={{
								height: "100%",
								p: 1.5,
								display: "flex",
								flexDirection: "column",
								minHeight: 0,
							}}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									mb: 1,
									flexWrap: "wrap",
								}}
							>
								<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
									{selectedPO
										? `Items — Order ${selectedPO.Number?.trim()}`
										: "Items"}
								</Typography>
								<Box sx={{ flex: 1 }} />
								<WriteLockTooltip locked={writesLocked}>
									<Button
										size="small"
										variant="contained"
										startIcon={<AddIcon />}
										onClick={openNewItem}
										disabled={writeDisabled || !selectedPO || selectedPODeleted}
									>
										New Item
									</Button>
								</WriteLockTooltip>
								<WriteLockTooltip locked={writesLocked}>
									<Button
										size="small"
										variant="outlined"
										startIcon={<EditIcon />}
										onClick={openEditItem}
										disabled={
											writeDisabled || !selectedItem || selectedPODeleted
										}
									>
										Edit
									</Button>
								</WriteLockTooltip>
								<WriteLockTooltip locked={writesLocked}>
									<Button
										size="small"
										variant="outlined"
										color="error"
										startIcon={<DeleteIcon />}
										onClick={() =>
											selectedItem &&
											setConfirm({
												kind: "item",
												id: selectedItem.Id,
												label: `#${selectedItem.ItemNumber}`,
											})
										}
										disabled={
											writeDisabled || !selectedItem || selectedPODeleted
										}
									>
										Delete
									</Button>
								</WriteLockTooltip>
							</Box>
							<Box sx={{ flex: 1, width: "100%", minHeight: 0 }}>
								<PurchaseOrderDetailsTable
									items={detailsQuery.data ?? []}
									selectedId={selectedItem?.Id ?? null}
									onSelect={(item) => setFilters({ itemId: item.Id })}
									loading={detailsQuery.isFetching}
									rollBlanks={rollBlanksQuery.data ?? []}
								/>
							</Box>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<PurchaseOrderModal
				open={poModalOpen}
				purchaseOrder={editingPO}
				suppliers={suppliersQuery.data ?? []}
				costCenters={costCentersQuery.data ?? []}
				orderStatuses={orderStatusQuery.data ?? []}
				isSaving={isSavingPO}
				onClose={() => setPoModalOpen(false)}
				onSave={handleSavePO}
			/>

			{selectedPO && (
				<PurchaseOrderItemModal
					open={itemModalOpen}
					purchaseOrderId={selectedPO.Id}
					item={editingItem}
					rollBlanks={rollBlanksQuery.data ?? []}
					suppliers={suppliersQuery.data ?? []}
					costCenters={costCentersQuery.data ?? []}
					isSaving={isSavingItem}
					onClose={() => setItemModalOpen(false)}
					onSave={handleSaveItem}
				/>
			)}

			<DeleteConfirmModal
				open={!!confirm}
				title={confirm?.kind === "po" ? "Delete purchase order" : "Delete item"}
				itemName={
					confirm?.kind === "po"
						? // Deleting the order also deletes the rolls received against it —
							// say so, or the loss is a surprise. Rolls that were already
							// mounted or turned are protected by foreign keys: the delete
							// fails there and the order is only marked as deleted instead.
							`purchase order "${confirm.label}", its items and the rolls received against them`
						: `item ${confirm?.label ?? ""}`
				}
				isDeleting={removePO.isPending || removeItem.isPending}
				onConfirm={handleConfirmDelete}
				onCancel={() => setConfirm(null)}
			/>

			<Snackbar
				open={!!error}
				autoHideDuration={6000}
				onClose={() => setError(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="error" onClose={() => setError(null)}>
					{error}
				</Alert>
			</Snackbar>

			<Snackbar
				open={!!warning}
				autoHideDuration={8000}
				onClose={() => setWarning(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="warning" onClose={() => setWarning(null)}>
					{warning}
				</Alert>
			</Snackbar>
		</Box>
	);
};
